import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

const MESSAGE_GROUP_WINDOW_MS = 5 * 60 * 1000
const TIMESTAMP_SEPARATOR_MS = 20 * 60 * 1000
const LONG_PRESS_MS = 400
const SWIPE_TIME_THRESHOLD_PX = 40
const CHAT_SYNC_POLL_MS = 12_000
const CHAT_CABLE_WATCHDOG_MS = 5_000

export default class extends Controller {
  static targets = [
    "messagesPanel",
    "messagesScroll",
    "messageList",
    "emptyState",
    "loadOlderWrap",
    "loadOlderBtn",
    "form",
    "input",
    "sendBtn",
    "composer",
    "readonlyBanner",
    "replyPreview",
    "replyPreviewUsername",
    "replyPreviewBody",
    "emojiOverlay",
    "emojiPicker",
    "messageTemplate",
    "mobileSheetOverlay",
    "mobileSheet",
    "mobileSheetReactions",
    "mobileSheetPreview",
    "mobileSheetTime",
    "mobileDeleteBtn",
    "onlineWrap",
    "onlineToggle",
    "onlinePanel",
    "onlineList",
    "onlineCount"
  ]

  static values = {
    activityId: Number,
    messagesUrl: String,
    presenceUrl: String,
    writable: Boolean,
    emojis: Array,
    currentUserId: Number
  }

  connect() {
    this.replyParentId = null
    this.replyHighlightArticle = null
    this.activeReactionMessageId = null
    this.mobileSheetArticle = null
    this.oldestId = null
    this.hasMore = false
    this.messageNodes = new Map()
    this.longPressTimer = null
    this.longPressArticle = null
    this.touchStartX = 0
    this.touchStartY = 0
    this.swipeTimesActive = false
    this.latestMessageId = null
    this.cableConnected = false
    this.cableHasConnected = false
    this.pollTimer = null
    this.cableWatchdogTimer = null
    this.boundVisibility = this.#onVisibility.bind(this)

    this.#applyWritableState()
    this.#loadMessages()
    this.#loadPresence()
    this.#connectCable()
    this.#startCableWatchdog()
    this.#bindPointerListeners()
    this.#syncChatPageScrollLock()
    document.addEventListener("visibilitychange", this.boundVisibility)
  }

  disconnect() {
    if (this.boundOnlineReposition) {
      window.removeEventListener("resize", this.boundOnlineReposition)
    }
    this.#closeOnlinePanel()
    this.closeMobileSheet()
    this.#cancelLongPress()
    this.#disconnectCable()
    this.#stopFallbackPolling()
    this.#stopCableWatchdog()
    this.#unbindPointerListeners()
    this.#syncChatPageScrollLock(false)
    document.removeEventListener("visibilitychange", this.boundVisibility)
  }

  keydown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      this.formTarget.requestSubmit()
    }
  }

  preventMessageContextMenu(event) {
    if (event.target.closest("article.activity-chat__message")) {
      event.preventDefault()
    }
  }

  toggleOnlinePanel(event) {
    event?.preventDefault()
    if (!this.hasOnlinePanelTarget) return

    const opening = this.onlinePanelTarget.classList.contains("hidden")
    if (opening) {
      this.#openOnlinePanel()
    } else {
      this.#closeOnlinePanel()
    }
  }

  closeMobileSheet() {
    this.mobileSheetOverlayTarget.classList.add("hidden")
    this.mobileSheetTarget.classList.add("hidden")
    this.mobileSheetArticle = null
    this.#syncMobileSheetOpen(false)
  }

  mobileReply() {
    const article = this.mobileSheetArticle
    this.closeMobileSheet()
    if (!article) return

    this.startReply({ target: article.querySelector('[data-role="reply-btn"]') || article })
  }

  async mobileDelete() {
    const article = this.mobileSheetArticle
    this.closeMobileSheet()
    if (!article) return

    await this.deleteMessage({ target: article.querySelector('[data-role="delete-btn"]') || article })
  }

  async sendMessage(event) {
    event.preventDefault()
    if (!this.writableValue) return

    const body = this.inputTarget.value.trim()
    if (!body) return

    this.sendBtnTarget.disabled = true
    const payload = { message: { body } }
    if (this.replyParentId) payload.message.parent_id = this.replyParentId

    try {
      const response = await fetch(this.messagesUrlValue, {
        method: "POST",
        headers: this.#jsonHeaders(),
        body: JSON.stringify(payload),
        credentials: "same-origin"
      })

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        alert(data.errors?.join("\n") || "Could not send message.")
        return
      }

      const data = await response.json()
      if (this.messageNodes.has(data.message.id)) {
        this.#updateMessageNode(data.message)
      } else {
        this.#appendMessage(data.message, { scroll: true })
      }
      this.inputTarget.value = ""
      this.cancelReply()
    } finally {
      this.sendBtnTarget.disabled = false
    }
  }

  async loadOlder() {
    if (!this.hasMore || !this.oldestId) return

    this.loadOlderBtnTarget.disabled = true
    const url = `${this.messagesUrlValue}?before_id=${this.oldestId}`

    try {
      const response = await fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      const data = await response.json()
      const scroller = this.#scrollContainer()
      const previousHeight = scroller.scrollHeight

      data.messages.forEach((message) => this.#prependMessage(message))

      this.hasMore = data.has_more
      this.oldestId = data.messages[0]?.id ?? this.oldestId
      this.#toggleLoadOlder()

      scroller.scrollTop = scroller.scrollHeight - previousHeight
    } finally {
      this.loadOlderBtnTarget.disabled = false
    }
  }

  startReply(event) {
    const article = event.target.closest("[data-message-id]")
    if (!article) return

    this.#closeAllReactionPickers()
    this.closeMobileSheet()
    this.replyParentId = parseInt(article.dataset.messageId, 10)
    const bodyEl = article.querySelector('[data-role="body"]')
    this.replyPreviewUsernameTarget.textContent = article.dataset.username
    this.replyPreviewBodyTarget.textContent = bodyEl?.textContent?.trim() || "Message removed"
    this.replyPreviewTarget.classList.remove("hidden")
    this.#setReplyHighlight(article)
    this.inputTarget.placeholder = `Reply to ${article.dataset.username}…`
    this.inputTarget.focus()
  }

  cancelReply() {
    this.replyParentId = null
    this.replyPreviewTarget.classList.add("hidden")
    this.#clearReplyHighlight()
    this.inputTarget.placeholder = "Message attendees…"
  }

  closeReactionPickers() {
    this.#closeAllReactionPickers()
  }

  stopPickerClick(event) {
    event.stopPropagation()
  }

  toggleReactionPicker(event) {
    event.stopPropagation()
    const article = event.target.closest("[data-message-id]")
    if (!article) return

    const messageId = article.dataset.messageId
    const isOpen =
      !this.emojiPickerTarget.classList.contains("hidden") &&
      this.activeReactionMessageId === messageId

    this.#closeAllReactionPickers()
    this.closeMobileSheet()
    if (isOpen) return

    this.activeReactionMessageId = messageId
    this.#buildReactionPicker()
    this.#positionEmojiPicker(event.currentTarget)
    this.emojiOverlayTarget.classList.remove("hidden")
  }

  async deleteMessage(event) {
    const article = event.target.closest("[data-message-id]")
    if (!article || !confirm("Delete this message?")) return

    const url = `${this.messagesUrlValue}/${article.dataset.messageId}`
    const response = await fetch(url, {
      method: "DELETE",
      headers: this.#jsonHeaders(),
      credentials: "same-origin"
    })

    if (response.ok) {
      const data = await response.json()
      this.#updateMessageNode(data.message)
    }
  }

  #connectCable() {
    const cableUrl = document.querySelector('meta[name="action-cable-url"]')?.content || "/cable"
    this.consumer = createConsumer(cableUrl)
    this.subscription = this.consumer.subscriptions.create(
      { channel: "SportActivityChatChannel", sport_activity_id: this.activityIdValue },
      {
        connected: () => {
          this.#stopCableWatchdog()
          if (this.cableHasConnected) this.#syncNewMessages()
          this.cableHasConnected = true
          this.cableConnected = true
          this.#stopFallbackPolling()
        },
        disconnected: () => {
          this.cableConnected = false
          this.#startFallbackPolling()
        },
        received: (data) => this.#onCableMessage(data)
      }
    )
  }

  #disconnectCable() {
    this.subscription?.unsubscribe()
    this.consumer?.disconnect()
    this.subscription = null
    this.consumer = null
    this.cableConnected = false
  }

  #onVisibility() {
    if (document.visibilityState === "visible") this.#syncNewMessages()
  }

  #startFallbackPolling() {
    if (this.pollTimer) return
    this.pollTimer = window.setInterval(() => {
      if (!this.cableConnected) this.#syncNewMessages()
    }, CHAT_SYNC_POLL_MS)
  }

  #stopFallbackPolling() {
    if (!this.pollTimer) return
    window.clearInterval(this.pollTimer)
    this.pollTimer = null
  }

  #startCableWatchdog() {
    this.#stopCableWatchdog()
    this.cableWatchdogTimer = window.setTimeout(() => {
      if (!this.cableConnected) this.#startFallbackPolling()
    }, CHAT_CABLE_WATCHDOG_MS)
  }

  #stopCableWatchdog() {
    if (!this.cableWatchdogTimer) return
    window.clearTimeout(this.cableWatchdogTimer)
    this.cableWatchdogTimer = null
  }

  async #loadPresence() {
    if (!this.hasPresenceUrlValue) return

    try {
      const response = await fetch(this.presenceUrlValue, {
        headers: { Accept: "application/json" },
        credentials: "same-origin"
      })
      if (!response.ok) return

      const data = await response.json()
      this.#renderOnlineUsers(data.users)
    } catch {
      // ignore transient network errors
    }
  }

  async #syncNewMessages() {
    if (!this.latestMessageId) return

    try {
      const url = `${this.messagesUrlValue}?after_id=${this.latestMessageId}`
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        credentials: "same-origin"
      })
      if (!response.ok) return

      const data = await response.json()
      if (data.writable !== undefined) {
        this.writableValue = data.writable
        this.#applyWritableState()
      }

      data.messages.forEach((message) => {
        if (this.messageNodes.has(message.id)) {
          this.#updateMessageNode(message)
        } else {
          this.#appendMessage(message, { scroll: this.#isNearBottom() })
        }
      })
    } catch {
      // ignore transient network errors during background sync
    }
  }

  #onCableMessage(data) {
    switch (data.event) {
      case "presence_changed":
        this.#renderOnlineUsers(data.users)
        break
      case "message_created":
        if (this.messageNodes.has(data.message.id)) {
          this.#updateMessageNode(data.message)
        } else {
          this.#appendMessage(data.message, { scroll: this.#isNearBottom() })
        }
        break
      case "message_deleted":
        this.#updateMessageNode(data.message)
        break
      case "reaction_changed":
        this.#updateReactions(data.message_id, data.reactions)
        break
    }
  }

  async #loadMessages() {
    const response = await fetch(this.messagesUrlValue, {
      headers: { Accept: "application/json" },
      credentials: "same-origin"
    })
    const data = await response.json()

    this.writableValue = data.writable
    this.#applyWritableState()

    this.hasMore = data.has_more
    this.oldestId = data.messages[0]?.id ?? null

    data.messages.forEach((message) => this.#appendMessage(message, { scroll: false, refreshLayout: false }))
    this.#refreshLayout()
    this.#toggleLoadOlder()
    this.#toggleEmpty()
    this.#scrollToBottom()
    this.#rememberLatestMessageId(data.messages.at(-1)?.id)
  }

  #appendMessage(message, { scroll, refreshLayout = true }) {
    if (this.messageNodes.has(message.id)) return

    const node = this.#buildMessageNode(message)
    this.messageListTarget.appendChild(node)
    this.messageNodes.set(message.id, node)
    this.#rememberLatestMessageId(message.id)
    this.#toggleEmpty()
    if (refreshLayout) this.#refreshLayout()

    if (scroll) this.#scrollToBottom()
  }

  #prependMessage(message) {
    if (this.messageNodes.has(message.id)) return

    const node = this.#buildMessageNode(message)
    this.messageListTarget.insertBefore(node, this.messageListTarget.firstChild)
    this.messageNodes.set(message.id, node)
    this.#refreshLayout()
  }

  #buildMessageNode(message) {
    const normalized = this.#normalizeIncomingMessage(message)
    const fragment = this.messageTemplateTarget.content.cloneNode(true)
    const article = fragment.querySelector("article")
    article.dataset.messageId = message.id
    article.dataset.username = message.user.username
    article.dataset.userId = message.user.id
    article.dataset.createdAt = message.created_at
    article.dataset.canDelete = normalized.can_delete ? "true" : "false"
    if (this.#isMine(normalized)) article.classList.add("activity-chat__message--mine")

    const avatarWrap = article.querySelector('[data-role="avatar"]')
    this.#renderAvatar(avatarWrap, normalized.user)

    article.querySelector('[data-role="username"]').textContent = normalized.user.username

    const hoverTime = article.querySelector('[data-role="hover-time"]')
    const hoverFormatted = this.#formatTimestampSeparator(normalized.created_at)
    hoverTime.textContent = hoverFormatted.visual
    hoverTime.dateTime = normalized.created_at
    hoverTime.title = hoverFormatted.full

    const bodyEl = article.querySelector('[data-role="body"]')
    if (normalized.deleted) {
      article.classList.add("activity-chat__message--deleted")
      bodyEl.textContent = "Message removed"
    } else {
      bodyEl.textContent = normalized.body
    }

    if (normalized.parent) {
      const quote = article.querySelector('[data-role="quote"]')
      quote.classList.remove("hidden")
      quote.textContent = `${normalized.parent.username}: ${normalized.parent.body || "Message removed"}`
    }

    if (normalized.can_delete) {
      article.querySelector('[data-role="delete-btn"]').classList.remove("hidden")
    }

    this.#renderReactions(article, normalized.reactions)

    if (!this.writableValue) {
      article.querySelector('[data-role="reply-btn"]')?.classList.add("hidden")
      article.querySelector('[data-role="react-btn"]')?.classList.add("hidden")
    }

    return article
  }

  #updateMessageNode(message) {
    const existing = this.messageNodes.get(message.id)
    if (!existing) return

    const updated = this.#buildMessageNode(message)
    existing.replaceWith(updated)
    this.messageNodes.set(message.id, updated)
    this.#rememberLatestMessageId(message.id)
    this.#refreshLayout()
  }

  #rememberLatestMessageId(id) {
    if (!id) return
    const numericId = Number(id)
    if (!this.latestMessageId || numericId > this.latestMessageId) {
      this.latestMessageId = numericId
    }
  }

  #normalizeIncomingMessage(message) {
    const mine = message.mine ?? this.#isMine(message)
    return {
      ...message,
      mine,
      can_delete: message.can_delete ?? mine
    }
  }

  #updateReactions(messageId, reactions) {
    const article = this.messageNodes.get(parseInt(messageId, 10))
    if (!article) return
    this.#renderReactions(article, reactions)
    this.#refreshLayout()
  }

  #renderReactions(article, reactions) {
    const container = article.querySelector('[data-role="reactions"]')
    container.replaceChildren()

    Object.entries(reactions || {}).forEach(([emoji, data]) => {
      const btn = document.createElement("button")
      btn.type = "button"
      btn.className = "activity-chat__reaction rounded-full border border-slate-200 px-2 py-0.5 text-xs dark:border-zinc-600"
      if (data.user_ids.includes(this.currentUserIdValue)) {
        btn.classList.add("bg-slate-200", "dark:bg-zinc-700")
      }
      btn.textContent = `${emoji} ${data.count}`
      btn.title = data.usernames.join(", ")
      btn.addEventListener("click", () => this.#toggleReaction(article.dataset.messageId, emoji))
      container.appendChild(btn)
    })
  }

  async #toggleReaction(messageId, emoji) {
    const url = `${this.messagesUrlValue}/${messageId}/toggle_reaction`
    await fetch(url, {
      method: "POST",
      headers: this.#jsonHeaders(),
      body: JSON.stringify({ emoji }),
      credentials: "same-origin"
    })

    this.#closeAllReactionPickers()
    this.closeMobileSheet()
  }

  #buildReactionPicker() {
    const picker = this.emojiPickerTarget
    if (picker.dataset.built) return

    picker.dataset.built = "1"
    this.emojisValue.forEach((emoji) => {
      const btn = document.createElement("button")
      btn.type = "button"
      btn.className = "text-lg hover:scale-110 transition-transform px-1.5 py-0.5"
      btn.textContent = emoji
      btn.addEventListener("click", (event) => {
        event.stopPropagation()
        if (!this.activeReactionMessageId) return
        this.#toggleReaction(this.activeReactionMessageId, emoji)
      })
      picker.appendChild(btn)
    })
  }

  #positionEmojiPicker(anchor) {
    const picker = this.emojiPickerTarget
    picker.classList.remove("hidden")
    picker.style.visibility = "hidden"

    const rect = anchor.getBoundingClientRect()
    const pickerRect = picker.getBoundingClientRect()
    const margin = 8

    let top = rect.bottom + margin
    let left = rect.left

    if (top + pickerRect.height > window.innerHeight - margin) {
      top = rect.top - pickerRect.height - margin
    }

    if (left + pickerRect.width > window.innerWidth - margin) {
      left = window.innerWidth - pickerRect.width - margin
    }

    left = Math.max(margin, left)

    picker.style.top = `${top}px`
    picker.style.left = `${left}px`
    picker.style.visibility = "visible"
  }

  #closeAllReactionPickers() {
    this.emojiOverlayTarget?.classList.add("hidden")
    this.emojiPickerTarget?.classList.add("hidden")
    this.activeReactionMessageId = null
  }

  #openMobileSheet(article) {
    this.mobileSheetArticle = article
    const bodyEl = article.querySelector('[data-role="body"]')
    this.mobileSheetPreviewTarget.textContent = bodyEl?.textContent?.trim() || "Message removed"
    this.mobileSheetTimeTarget.textContent = this.#formatTimestampSeparator(article.dataset.createdAt).visual

    const reactions = this.mobileSheetReactionsTarget
    reactions.replaceChildren()
    this.emojisValue.forEach((emoji) => {
      const btn = document.createElement("button")
      btn.type = "button"
      btn.className = "text-2xl px-2 py-1"
      btn.textContent = emoji
      btn.addEventListener("click", (event) => {
        event.stopPropagation()
        this.#toggleReaction(article.dataset.messageId, emoji)
      })
      reactions.appendChild(btn)
    })

    if (article.dataset.canDelete === "true") {
      this.mobileDeleteBtnTarget.classList.remove("hidden")
    } else {
      this.mobileDeleteBtnTarget.classList.add("hidden")
    }

    this.mobileSheetOverlayTarget.classList.remove("hidden")
    this.mobileSheetTarget.classList.remove("hidden")
    this.#syncMobileSheetOpen(true)
  }

  #setReplyHighlight(article) {
    this.#clearReplyHighlight()
    this.replyHighlightArticle = article
    article.classList.add("activity-chat__message--replying")
  }

  #clearReplyHighlight() {
    this.replyHighlightArticle?.classList.remove("activity-chat__message--replying")
    this.replyHighlightArticle = null
  }

  #applyWritableState() {
    if (this.writableValue) {
      this.readonlyBannerTarget.classList.add("hidden")
      this.composerTarget.classList.remove("hidden", "opacity-50", "pointer-events-none")
      this.inputTarget.disabled = false
    } else {
      this.readonlyBannerTarget.classList.remove("hidden")
      this.composerTarget.classList.add("opacity-50", "pointer-events-none")
      this.inputTarget.disabled = true
    }
  }

  #toggleLoadOlder() {
    if (this.hasMore) {
      this.loadOlderWrapTarget.classList.remove("hidden")
    } else {
      this.loadOlderWrapTarget.classList.add("hidden")
    }
  }

  #toggleEmpty() {
    if (this.messageNodes.size === 0) {
      this.emptyStateTarget.classList.remove("hidden")
    } else {
      this.emptyStateTarget.classList.add("hidden")
    }
  }

  #scrollContainer() {
    return this.hasMessagesScrollTarget ? this.messagesScrollTarget : this.messageListTarget
  }

  #scrollToBottom() {
    const el = this.#scrollContainer()
    el.scrollTop = el.scrollHeight
  }

  #isNearBottom() {
    const el = this.#scrollContainer()
    return el.scrollHeight - el.scrollTop - el.clientHeight < 80
  }

  #isMobileChatLayout() {
    return window.matchMedia("(max-width: 767px)").matches
  }

  #isMine(message) {
    return Number(message.user?.id) === this.currentUserIdValue
  }

  #useMobileMessageGestures() {
    if (window.matchMedia("(max-width: 767px)").matches) return true

    const main = document.querySelector("main.activity-chat-page")
    return Boolean(main && getComputedStyle(main).position === "fixed")
  }

  #isInteractiveMessageTarget(target) {
    return Boolean(target.closest("button, a, input, textarea, [data-role='actions']"))
  }

  #bindPointerListeners() {
    if (!this.hasMessageListTarget || !this.#useMobileMessageGestures()) return

    this.boundTouchStart = this.#onMessageTouchStart.bind(this)
    this.boundTouchMove = this.#onMessageTouchMove.bind(this)
    this.boundTouchEnd = this.#onMessageTouchEnd.bind(this)

    this.messageListTarget.addEventListener("touchstart", this.boundTouchStart, { passive: true })
    this.messageListTarget.addEventListener("touchmove", this.boundTouchMove, { passive: true })
    this.messageListTarget.addEventListener("touchend", this.boundTouchEnd, { passive: true })
    this.messageListTarget.addEventListener("touchcancel", this.boundTouchEnd, { passive: true })
  }

  #unbindPointerListeners() {
    this.#cancelLongPress()
    if (!this.hasMessageListTarget || !this.boundTouchStart) return

    this.messageListTarget.removeEventListener("touchstart", this.boundTouchStart)
    this.messageListTarget.removeEventListener("touchmove", this.boundTouchMove)
    this.messageListTarget.removeEventListener("touchend", this.boundTouchEnd)
    this.messageListTarget.removeEventListener("touchcancel", this.boundTouchEnd)
    this.boundTouchStart = null
    this.boundTouchMove = null
    this.boundTouchEnd = null
  }

  #onMessageTouchStart(event) {
    if (event.touches.length !== 1) return

    const article = event.target.closest("article.activity-chat__message")
    if (!article || this.#isInteractiveMessageTarget(event.target)) return

    const touch = event.touches[0]
    this.#beginLongPress(article, touch)
  }

  #onMessageTouchMove(event) {
    if (!this.longPressTimer || event.touches.length !== 1) return

    const touch = event.touches[0]
    if (Math.abs(touch.clientX - this.touchStartX) > 10 || Math.abs(touch.clientY - this.touchStartY) > 10) {
      this.#cancelLongPress()
    }
  }

  #onMessageTouchEnd() {
    if (this.longPressTimer) this.#cancelLongPress()
    else this.#endLongPressTracking()
  }

  #beginLongPress(article, touch) {
    this.#endLongPressTracking()

    this.longPressArticle = article
    this.touchStartX = touch.clientX
    this.touchStartY = touch.clientY
    this.swipeTimesActive = false

    this.longPressTimer = window.setTimeout(() => {
      this.longPressTimer = null
      window.getSelection()?.removeAllRanges()
      this.#openMobileSheet(article)
    }, LONG_PRESS_MS)
  }

  #cancelLongPress() {
    if (this.longPressTimer) {
      window.clearTimeout(this.longPressTimer)
      this.longPressTimer = null
    }
    this.#endLongPressTracking()
    this.#hideSwipeTimes()
  }

  #endLongPressTracking() {
    this.longPressArticle = null
  }

  #syncChatPageScrollLock(force) {
    const shouldLock = force ?? true
    document.body.classList.toggle("activity-chat-page-open", shouldLock)
  }

  #syncMobileSheetOpen(force) {
    const isOpen = force ?? false
    document.body.classList.toggle("activity-chat-mobile-sheet-open", isOpen)
  }

  #hideSwipeTimes() {
    this.swipeTimesActive = false
    this.messageListTarget.classList.remove("activity-chat__show-times")
  }

  #refreshLayout() {
    this.#refreshMessageGrouping()
    this.#refreshTimestampDividers()
  }

  #refreshMessageGrouping() {
    const articles = Array.from(this.messageListTarget.querySelectorAll("article.activity-chat__message"))
    const groupClasses = [
      "activity-chat__message--group-single",
      "activity-chat__message--group-first",
      "activity-chat__message--group-middle",
      "activity-chat__message--group-last",
      "activity-chat__message--sender-break",
      "activity-chat__message--same-sender-pause"
    ]

    articles.forEach((article, index) => {
      const prev = articles[index - 1]
      const next = articles[index + 1]
      const groupsWithPrev = Boolean(prev && this.#messagesGroup(prev, article))
      const groupsWithNext = Boolean(next && this.#messagesGroup(article, next))
      const runWithPrev = Boolean(prev && this.#sameSenderRunAdjacent(prev, article))
      const runWithNext = Boolean(next && this.#sameSenderRunAdjacent(article, next))
      const isMine = article.classList.contains("activity-chat__message--mine")
      const showAvatar = !isMine && !runWithNext
      const showMeta = !isMine && !runWithPrev

      article.classList.remove(...groupClasses)
      if (prev && this.#needsSameSenderPauseGap(prev, article)) {
        article.classList.add("activity-chat__message--same-sender-pause")
      }
      if (!groupsWithPrev && prev) {
        article.classList.add("activity-chat__message--sender-break")
      }
      if (!groupsWithPrev && !groupsWithNext) {
        article.classList.add("activity-chat__message--group-single")
      } else if (!groupsWithPrev && groupsWithNext) {
        article.classList.add("activity-chat__message--group-first")
      } else if (groupsWithPrev && groupsWithNext) {
        article.classList.add("activity-chat__message--group-middle")
      } else {
        article.classList.add("activity-chat__message--group-last")
      }

      article.classList.toggle("activity-chat__message--avatar-hidden", !showAvatar)
      article.classList.toggle("activity-chat__message--meta-hidden", !showMeta)
      article.querySelector(".activity-chat__meta")?.classList.toggle("hidden", !showMeta)
    })
  }

  #refreshTimestampDividers() {
    this.messageListTarget.querySelectorAll(".activity-chat__timestamp").forEach((node) => node.remove())

    const articles = Array.from(this.messageListTarget.querySelectorAll("article.activity-chat__message"))
    articles.forEach((article, index) => {
      const prev = articles[index - 1]
      if (!this.#needsTimestampSeparator(prev, article)) return

      article.before(this.#buildTimestampDivider(article.dataset.createdAt))
    })
  }

  #needsTimestampSeparator(prevArticle, article) {
    if (!prevArticle) return true

    const prevAt = Date.parse(prevArticle.dataset.createdAt)
    const at = Date.parse(article.dataset.createdAt)
    if (Number.isNaN(prevAt) || Number.isNaN(at)) return true
    if (!this.#isSameCalendarDay(prevAt, at)) return true

    return at - prevAt > TIMESTAMP_SEPARATOR_MS
  }

  #buildTimestampDivider(iso) {
    const formatted = this.#formatTimestampSeparator(iso)
    const divider = document.createElement("div")
    divider.className = "activity-chat__timestamp"
    divider.setAttribute("role", "separator")
    divider.innerHTML = `<span class="sr-only">${formatted.full}</span><span aria-hidden="true">${formatted.visual}</span>`
    return divider
  }

  #sameSenderRunAdjacent(earlier, later) {
    if (String(earlier.dataset.userId) !== String(later.dataset.userId)) return false
    if (this.#hasTimestampBetween(earlier, later)) return false

    const earlierAt = Date.parse(earlier.dataset.createdAt)
    const laterAt = Date.parse(later.dataset.createdAt)
    if (Number.isNaN(earlierAt) || Number.isNaN(laterAt)) return false
    if (!this.#isSameCalendarDay(earlierAt, laterAt)) return false

    return laterAt - earlierAt <= TIMESTAMP_SEPARATOR_MS
  }

  #hasTimestampBetween(earlier, later) {
    let node = earlier.nextElementSibling
    while (node && node !== later) {
      if (node.classList.contains("activity-chat__timestamp")) return true
      node = node.nextElementSibling
    }
    return false
  }

  #messagesGroup(earlier, later) {
    if (String(earlier.dataset.userId) !== String(later.dataset.userId)) return false
    if (this.#messageHasReply(later)) return false
    if (this.#messageHasReactions(earlier)) return false

    const earlierAt = Date.parse(earlier.dataset.createdAt)
    const laterAt = Date.parse(later.dataset.createdAt)
    if (Number.isNaN(earlierAt) || Number.isNaN(laterAt)) return false

    return laterAt - earlierAt <= MESSAGE_GROUP_WINDOW_MS
  }

  #messageHasReply(article) {
    const quote = article.querySelector('[data-role="quote"]')
    return Boolean(quote && !quote.classList.contains("hidden"))
  }

  #messageHasReactions(article) {
    const reactions = article.querySelector('[data-role="reactions"]')
    return Boolean(reactions && reactions.childElementCount > 0)
  }

  #needsSameSenderPauseGap(prevArticle, article) {
    if (String(prevArticle.dataset.userId) !== String(article.dataset.userId)) return false
    if (this.#messagesGroup(prevArticle, article)) return false
    if (this.#needsTimestampSeparator(prevArticle, article)) return false

    const prevAt = Date.parse(prevArticle.dataset.createdAt)
    const at = Date.parse(article.dataset.createdAt)
    if (Number.isNaN(prevAt) || Number.isNaN(at)) return false

    const gap = at - prevAt
    return gap > MESSAGE_GROUP_WINDOW_MS && gap <= TIMESTAMP_SEPARATOR_MS
  }

  #isSameCalendarDay(aMs, bMs) {
    const a = new Date(aMs)
    const b = new Date(bMs)
    return a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
  }

  #startOfDay(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate())
  }

  #formatTimestampSeparator(iso) {
    const date = new Date(iso)
    const now = new Date()
    const startOfToday = this.#startOfDay(now)
    const startOfYesterday = new Date(startOfToday)
    startOfYesterday.setDate(startOfYesterday.getDate() - 1)
    const weekAgo = new Date(startOfToday)
    weekAgo.setDate(weekAgo.getDate() - 6)

    const time = this.#formatClock(date)
    const full = this.#formatFullDateTime(date)

    if (date >= startOfToday) {
      return { visual: time, full }
    }

    if (date >= startOfYesterday) {
      return { visual: full, full }
    }

    if (date >= weekAgo) {
      const weekday = date.toLocaleDateString(undefined, { weekday: "short" })
      return { visual: `${weekday} ${time}`, full }
    }

    return { visual: full, full }
  }

  #formatClock(date) {
    return date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false })
  }

  #formatFullDateTime(date) {
    const day = date.toLocaleDateString(undefined, { day: "numeric", month: "short" })
    const time = this.#formatClock(date)
    return `${day} at ${time}`
  }

  #openOnlinePanel() {
    if (!this.hasOnlinePanelTarget) return

    this.onlinePanelTarget.classList.remove("hidden")
    this.#positionOnlinePanel()
    if (this.hasOnlineToggleTarget) {
      this.onlineToggleTarget.setAttribute("aria-expanded", "true")
      this.onlineToggleTarget.classList.add("activity-chat__online-toggle--open")
    }

    if (!this.boundOnlineOutsideClick) {
      this.boundOnlineOutsideClick = this.#onlineOutsideClick.bind(this)
    }
    if (!this.boundOnlineReposition) {
      this.boundOnlineReposition = this.#positionOnlinePanel.bind(this)
    }
    window.addEventListener("resize", this.boundOnlineReposition)
    queueMicrotask(() => {
      document.addEventListener("click", this.boundOnlineOutsideClick, true)
    })
  }

  #closeOnlinePanel() {
    if (!this.hasOnlinePanelTarget) return

    this.onlinePanelTarget.classList.add("hidden")
    this.onlinePanelTarget.classList.remove("activity-chat__online-panel--fixed")
    this.onlinePanelTarget.style.removeProperty("top")
    this.onlinePanelTarget.style.removeProperty("right")
    if (this.hasOnlineToggleTarget) {
      this.onlineToggleTarget.setAttribute("aria-expanded", "false")
      this.onlineToggleTarget.classList.remove("activity-chat__online-toggle--open")
    }
    if (this.boundOnlineOutsideClick) {
      document.removeEventListener("click", this.boundOnlineOutsideClick, true)
    }
    if (this.boundOnlineReposition) {
      window.removeEventListener("resize", this.boundOnlineReposition)
    }
  }

  #positionOnlinePanel() {
    if (!this.hasOnlinePanelTarget || !this.hasOnlineToggleTarget) return
    if (this.onlinePanelTarget.classList.contains("hidden")) return

    const panel = this.onlinePanelTarget
    const mobile = window.matchMedia("(max-width: 767px)").matches

    if (!mobile) {
      panel.classList.remove("activity-chat__online-panel--fixed")
      panel.style.removeProperty("top")
      panel.style.removeProperty("right")
      return
    }

    const rect = this.onlineToggleTarget.getBoundingClientRect()
    panel.classList.add("activity-chat__online-panel--fixed")
    panel.style.top = `${rect.bottom + 8}px`
    panel.style.right = `${Math.max(12, window.innerWidth - rect.right)}px`
  }

  #onlineOutsideClick(event) {
    const root = this.hasOnlineWrapTarget
      ? this.onlineWrapTarget
      : this.onlineToggleTarget?.closest(".activity-chat__online")
    if (root && !root.contains(event.target)) {
      this.#closeOnlinePanel()
    }
  }

  #renderOnlineUsers(users) {
    if (!this.hasOnlineCountTarget || !this.hasOnlineListTarget) return

    const list = users || []
    const count = list.length
    const badge = this.onlineCountTarget
    badge.textContent = String(count)
    if (count > 0) {
      badge.classList.remove("invisible")
      badge.removeAttribute("aria-hidden")
    } else {
      badge.classList.add("invisible")
      badge.setAttribute("aria-hidden", "true")
    }
    if (this.hasOnlineToggleTarget) {
      this.onlineToggleTarget.setAttribute(
        "aria-label",
        count === 1 ? "1 person online in chat" : `${count} people online in chat`
      )
    }

    this.onlineListTarget.replaceChildren()
    if (list.length === 0) {
      const empty = document.createElement("li")
      empty.className = "activity-chat__online-empty"
      empty.textContent = "No one is in the chat right now"
      this.onlineListTarget.appendChild(empty)
      return
    }

    list.forEach((user) => {
      const item = document.createElement("li")
      item.className = "activity-chat__online-user"

      const avatar = document.createElement("span")
      avatar.className = "activity-chat__online-avatar"
      this.#renderPresenceAvatar(avatar, user)

      const name = document.createElement("span")
      name.className = "activity-chat__online-name"
      const isYou = Number(user.id) === this.currentUserIdValue
      name.textContent = isYou ? `${user.username} (you)` : user.username

      item.appendChild(avatar)
      item.appendChild(name)
      this.onlineListTarget.appendChild(item)
    })
  }

  #renderPresenceAvatar(container, user) {
    const initialClasses =
      "inline-flex h-7 w-7 cursor-default select-none items-center justify-center rounded-full bg-slate-200 text-xs font-semibold text-slate-600 ring-2 ring-slate-200 dark:bg-zinc-700 dark:text-slate-300 dark:ring-zinc-600"
    const showInitial = () => {
      container.replaceChildren()
      const initial = document.createElement("span")
      initial.className = initialClasses
      initial.textContent = user.username.charAt(0).toUpperCase()
      container.appendChild(initial)
    }

    if (!user.avatar_url) {
      showInitial()
      return
    }

    const img = document.createElement("img")
    img.src = user.avatar_url
    img.alt = ""
    img.className = "h-7 w-7 rounded-full object-cover ring-2 ring-slate-200 dark:ring-zinc-600"
    img.addEventListener("error", showInitial, { once: true })
    container.replaceChildren(img)
  }

  #renderAvatar(container, user) {
    const initialClasses =
      "inline-flex h-9 w-9 cursor-default select-none items-center justify-center rounded-full bg-slate-200 text-xs font-semibold text-slate-600 ring-2 ring-slate-200 dark:bg-zinc-700 dark:text-slate-300 dark:ring-zinc-600 sm:h-10 sm:w-10"
    const showInitial = () => {
      container.replaceChildren()
      const initial = document.createElement("span")
      initial.className = initialClasses
      initial.textContent = user.username.charAt(0).toUpperCase()
      container.appendChild(initial)
    }

    if (!user.avatar_url) {
      showInitial()
      return
    }

    const img = document.createElement("img")
    img.src = user.avatar_url
    img.alt = ""
    img.className = "h-9 w-9 rounded-full object-cover ring-2 ring-slate-200 dark:ring-zinc-600 sm:h-10 sm:w-10"
    img.addEventListener("error", showInitial, { once: true })
    container.replaceChildren(img)
  }

  #jsonHeaders() {
    return {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
    }
  }
}
